import { CHART_COLORS, CHART_FONT, CHART_VIEW } from "../constants";

// Parts both chart views share: the price scale, its grid and labels,
// the entry line and the markers

// Room on the right for the price labels
export const AXIS_WIDTH = 64;
const AXIS_GAP = 8;
const VERTICAL_PADDING = 12;
const MARKER_RADIUS = 5;
const MARKER_STROKE = 2;
const ENTRY_DASH = [4, 4];
const GRID_DASH = [2, 4];
const CROSSHAIR_DASH = [3, 3];
const CROSSHAIR_LABEL_GAP = 6;
// Max, middle and min of the price range
const AXIS_LABELS = 3;
// A flat market still gets a band to draw in
const FLAT_RANGE_PADDING = 0.001;

/**
 * Price to y and the plot size for a canvas of `width` × `height`.
 * @param {object} params
 * @param {number} params.width
 * @param {number} params.height
 * @param {number[]} params.prices every price the chart has to fit
 */
export const createFrame = ({ width, height, prices }) => {
  let min = Math.min(...prices);
  let max = Math.max(...prices);

  if (max <= min) {
    min *= 1 - FLAT_RANGE_PADDING;
    max *= 1 + FLAT_RANGE_PADDING;
  }

  const plotWidth = width - AXIS_WIDTH;
  const plotHeight = height - VERTICAL_PADDING * 2;

  return {
    min,
    max,
    plotWidth,
    plotHeight,
    top: VERTICAL_PADDING,
    bottom: VERTICAL_PADDING + plotHeight,
    toY: (price) =>
      VERTICAL_PADDING + ((max - price) / (max - min)) * plotHeight,
  };
};

/** Dotted grid lines with the max, middle and min prices on the right */
export const drawPriceAxis = (ctx, frame, formatPrice) => {
  const { min, max, plotWidth, toY } = frame;

  ctx.font = CHART_FONT;
  ctx.textBaseline = "middle";
  ctx.fillStyle = CHART_COLORS.label;
  ctx.strokeStyle = CHART_COLORS.grid;
  ctx.lineWidth = 1;
  ctx.setLineDash(GRID_DASH);

  for (let index = 0; index < AXIS_LABELS; index += 1) {
    const price = max - ((max - min) * index) / (AXIS_LABELS - 1);
    const y = Math.round(toY(price)) + 0.5;

    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(plotWidth, y);
    ctx.stroke();
    ctx.fillText(formatPrice(price), plotWidth + AXIS_GAP, y);
  }

  ctx.setLineDash([]);
};

export const drawMarker = (ctx, x, y, color) => {
  ctx.beginPath();
  ctx.arc(x, y, MARKER_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = MARKER_STROKE;
  ctx.strokeStyle = CHART_COLORS.markerStroke;
  ctx.stroke();
};

/**
 * Dashed line from the entry point to the right edge. The direction badge on
 * its start is HTML over the canvas, placed at the returned point.
 * @returns {{x: number, y: number}}
 */
export const drawEntry = (ctx, frame, x, entryPrice) => {
  const y = frame.toY(entryPrice);

  ctx.strokeStyle = CHART_COLORS.entry;
  ctx.lineWidth = 1;
  ctx.setLineDash(ENTRY_DASH);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(frame.plotWidth, y);
  ctx.stroke();
  ctx.setLineDash([]);

  return { x, y };
};

export const getToneColor = (isProfit) =>
  isProfit ? CHART_COLORS.up : CHART_COLORS.down;

// Index of the entry point, before the first candle
export const ENTRY_POINT_INDEX = -1;

/**
 * Width each candle takes. The line's entry sits on x = 0; the candles get
 * one more slot on the left for it.
 */
export const getCandleSlot = (view, plotWidth, count) =>
  view === CHART_VIEW.LINE ? plotWidth / count : plotWidth / (count + 1);

/**
 * X of a candle's point, ENTRY_POINT_INDEX for the entry: the end of its slot
 * for the line, the middle of it for candles.
 */
export const getCandleX = (view, index, plotWidth, count) => {
  const slot = getCandleSlot(view, plotWidth, count);
  const slotsBefore = view === CHART_VIEW.LINE ? index + 1 : index + 1.5;

  return slot * slotsBefore;
};

/**
 * A closed trade's line is laid out by time, from its entry to its exit, when
 * those times and the candle interval are known; otherwise by candle slots.
 */
export const isTimeBasedLine = ({
  view,
  exitPrice,
  entryTime,
  exitTime,
  intervalMs,
}) =>
  view === CHART_VIEW.LINE &&
  exitPrice != null &&
  entryTime != null &&
  exitTime != null &&
  intervalMs != null;

/**
 * X of a moment on the time based line: the entry on x = 0, the exit on the
 * right edge, kept within the plot.
 */
export const getLineTimeX = (timeMs, entryTime, exitTime, plotWidth) => {
  if (exitTime <= entryTime) return plotWidth;

  return Math.min(
    plotWidth,
    Math.max(0, (plotWidth * (timeMs - entryTime)) / (exitTime - entryTime)),
  );
};

/**
 * X of each candle's point on the time based line: at the candle's close
 * time, the last one (the exit) on the right edge.
 */
export const getLinePointXs = ({
  rangeCandles,
  intervalMs,
  entryTime,
  exitTime,
  plotWidth,
}) =>
  rangeCandles.map(({ time }, index) =>
    index === rangeCandles.length - 1
      ? plotWidth
      : getLineTimeX(time + intervalMs, entryTime, exitTime, plotWidth),
  );

/** Nearest point of the time based line under the pointer, the entry one included */
export const getLineIndexAtX = (x, params) => {
  const xs = [0, ...getLinePointXs(params)];
  let nearest = 0;

  xs.forEach((pointX, index) => {
    if (Math.abs(pointX - x) < Math.abs(xs[nearest] - x)) nearest = index;
  });

  return nearest - 1;
};

/** Nearest point under the pointer, the entry one included */
export const getCandleIndexAtX = (view, x, plotWidth, count) => {
  const slot = getCandleSlot(view, plotWidth, count);
  const index =
    view === CHART_VIEW.LINE
      ? Math.round(x / slot) - 1
      : Math.floor(x / slot) - 1;

  return Math.min(count - 1, Math.max(ENTRY_POINT_INDEX, index));
};

/** Dashed vertical line through the hovered point, its time above it */
export const drawCrosshair = (ctx, frame, { x, price, label }) => {
  const y = frame.toY(price);

  ctx.strokeStyle = CHART_COLORS.crosshair;
  ctx.lineWidth = 1;
  ctx.setLineDash(CROSSHAIR_DASH);
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, frame.top);
  ctx.lineTo(Math.round(x) + 0.5, frame.bottom);
  ctx.stroke();
  ctx.setLineDash([]);

  drawMarker(ctx, x, y, CHART_COLORS.crosshair);

  if (!label) return;

  ctx.font = CHART_FONT;
  ctx.fillStyle = CHART_COLORS.crosshair;
  ctx.textBaseline = "alphabetic";
  // Stays inside the plot near its edges
  ctx.textAlign = x > frame.plotWidth / 2 ? "right" : "left";
  ctx.fillText(
    label,
    x + (x > frame.plotWidth / 2 ? -CROSSHAIR_LABEL_GAP : CROSSHAIR_LABEL_GAP),
    frame.top,
  );
  ctx.textAlign = "left";
};
